import { useState, useEffect, useCallback, useRef } from 'react';
import { tokens } from '../tokens';
import MetaLabel from './MetaLabel';
import api from '../api';

const CHOICES = [
  { label: 'Struggling', value: 1 },
  { label: 'Running on fumes', value: 2 },
  { label: 'Somewhere in the middle', value: 3 },
  { label: 'Holding steady', value: 4 },
  { label: 'Doing better than expected', value: 5 },
];

export default function CheckInModal({ open, onClose, onCheckedIn }) {
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dismissTimer = useRef(null);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setPicked(null);
      setDone(false);
      setSubmitting(false);
    }
  }, [open]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  const handleSelect = useCallback(async (choice) => {
    if (submitting || picked !== null) return;
    setPicked(choice.value);
    setSubmitting(true);

    try {
      await api.post('/checkins', { emoji: choice.value });
      const { data: pulse } = await api.get('/checkins/pulse');
      // Transition to done state after 350ms
      setTimeout(() => {
        setDone(true);
        if (onCheckedIn) onCheckedIn(pulse);
        // Auto-dismiss after 1800ms
        dismissTimer.current = setTimeout(() => {
          onClose();
        }, 1800);
      }, 350);
    } catch {
      setTimeout(() => {
        setDone(true);
        if (onCheckedIn) onCheckedIn(null);
        dismissTimer.current = setTimeout(() => {
          onClose();
        }, 1800);
      }, 350);
    }
    setSubmitting(false);
  }, [submitting, picked, onClose, onCheckedIn]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(6, 4, 3, 0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '0 22px',
        animation: `gl-fade 400ms ${tokens.ease}`,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ animation: `gl-fade-up 600ms ${tokens.ease}` }}
      >
        {!done ? (
          <>
            <MetaLabel tokens={tokens} style={{ textAlign: 'center', marginBottom: 32 }}>
              Check in
            </MetaLabel>
            <h2 style={{
              fontFamily: tokens.fontDisplay, fontStyle: 'italic',
              fontSize: 30, lineHeight: 1.25, color: tokens.textHi,
              textAlign: 'center', margin: 0, textWrap: 'balance',
            }}>
              How are you<br/>holding up?
            </h2>
            <div style={{ marginTop: 48, display: 'flex', flexDirection: 'column' }}>
              {CHOICES.map((c) => {
                const isPicked = picked === c.value;
                const dim = picked !== null && !isPicked;
                return (
                  <button
                    key={c.value}
                    className="gl-btn-bare"
                    onClick={() => handleSelect(c)}
                    disabled={picked !== null}
                    style={{
                      padding: '16px 0',
                      textAlign: 'center',
                      fontFamily: tokens.fontDisplay,
                      fontSize: 20, letterSpacing: tokens.displayTracking,
                      color: isPicked ? tokens.accent : dim ? tokens.textDim : tokens.textHi,
                      opacity: dim ? 0.3 : 1,
                      transition: `all 350ms ${tokens.ease}`,
                      cursor: picked !== null ? 'default' : 'pointer',
                    }}
                    onMouseEnter={e => { if (picked === null) e.currentTarget.style.color = tokens.accent; }}
                    onMouseLeave={e => { if (picked === null) e.currentTarget.style.color = tokens.textHi; }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: tokens.fontDisplay, fontStyle: 'italic',
              fontSize: 24, color: tokens.textHi, lineHeight: 1.4,
            }}>
              Thanks for telling us.
            </div>
            <div style={{
              marginTop: 14, fontFamily: tokens.fontBody,
              fontSize: 14, color: tokens.textMid,
            }}>
              Your answer stays anonymous.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
